import React from 'react';
import { LEARNITIVE_LOCATION_CHANGE_EVENT } from './ws-boot-surface';

const COOKIE_PREFERENCE_KEY = 'interactive-cookie-consent-v1';
const STATCOUNTER_SCRIPT_ID = 'interactive-statcounter-script';
const GOOGLE_TAG_SCRIPT_ID = 'interactive-google-tag-script';
const STATCOUNTER_PROJECT = 12568371;
const STATCOUNTER_SECURITY = 'b4a422e2';
let statcounterLastRecordedPath: string | undefined;
let statcounterPendingPath: string | undefined;

type CookiePreference = 'accepted' | 'declined' | undefined;

type AnalyticsWindow = Window & {
  sc_project?: number;
  sc_invisible?: number;
  sc_security?: string;
  dataLayer?: unknown[];
  gtag?: (...args: unknown[]) => void;
  _statcounter?: { record_pageview?: () => void };
};

function currentPathname(): string {
  return window.location.pathname.replace(/\/+$/u, '') || '/';
}

function isAdminPath(pathname: string): boolean {
  return pathname === '/admin' || pathname.startsWith('/admin/');
}

function isMarketingPath(pathname: string): boolean {
  return pathname === '/' || [
    '/features',
    '/compare',
    '/use-cases',
    '/software-list',
    '/tools',
    '/templates',
    '/pricing',
    '/blog',
    '/docs',
    '/learn',
    '/about',
    '/contact',
    '/privacy',
    '/terms',
  ].some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

function isCanonicalProduction(): boolean {
  return window.location.protocol === 'https:' && window.location.hostname === 'www.learnitive.com';
}

function readCookiePreference(): CookiePreference {
  try {
    const value = window.localStorage.getItem(COOKIE_PREFERENCE_KEY);
    return value === 'accepted' || value === 'declined' ? value : undefined;
  } catch {
    return undefined;
  }
}

function writeCookiePreference(value: Exclude<CookiePreference, undefined>): void {
  try {
    window.localStorage.setItem(COOKIE_PREFERENCE_KEY, value);
  } catch {
    // Consent still applies for the current tab even when storage is unavailable.
  }
}

function recordStatcounterPageview(pathname: string): void {
  if (!isCanonicalProduction() || isAdminPath(pathname) || statcounterLastRecordedPath === pathname) return;
  const analyticsWindow = window as AnalyticsWindow;
  if (typeof analyticsWindow._statcounter?.record_pageview !== 'function') {
    statcounterPendingPath = pathname;
    return;
  }
  analyticsWindow._statcounter.record_pageview();
  statcounterLastRecordedPath = pathname;
  statcounterPendingPath = undefined;
}

function loadStatcounter(pathname: string): void {
  if (!isCanonicalProduction() || isAdminPath(pathname)) return;
  const existingScript = document.querySelector<HTMLScriptElement>('script[src="https://www.statcounter.com/counter/counter.js"]');
  if (existingScript) {
    if (statcounterLastRecordedPath === undefined) {
      // The default Statcounter snippet in index.html records the initial pageview.
      statcounterLastRecordedPath = pathname;
      existingScript.addEventListener('load', () => {
        const pendingPath = statcounterPendingPath;
        if (pendingPath && pendingPath === currentPathname() && !isAdminPath(pendingPath)) recordStatcounterPageview(pendingPath);
      }, { once: true });
      return;
    }
    recordStatcounterPageview(pathname);
    return;
  }
  if (document.getElementById(STATCOUNTER_SCRIPT_ID)) {
    recordStatcounterPageview(pathname);
    return;
  }

  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.sc_project = STATCOUNTER_PROJECT;
  analyticsWindow.sc_invisible = 1;
  analyticsWindow.sc_security = STATCOUNTER_SECURITY;
  // counter.js records the route active when the script first initializes.
  statcounterLastRecordedPath = pathname;

  const script = document.createElement('script');
  script.id = STATCOUNTER_SCRIPT_ID;
  script.async = true;
  script.src = 'https://www.statcounter.com/counter/counter.js';
  script.dataset.learnitiveAnalytics = 'statcounter';
  script.addEventListener('load', () => {
    const pendingPath = statcounterPendingPath;
    if (pendingPath && pendingPath === currentPathname() && !isAdminPath(pendingPath)) {
      recordStatcounterPageview(pendingPath);
    }
  }, { once: true });
  document.head.appendChild(script);
}

async function readGoogleTagId(): Promise<string> {
  try {
    const response = await fetch('/api/config/public', {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return '';
    const payload = await response.json() as { googleTagId?: unknown };
    return typeof payload.googleTagId === 'string' ? payload.googleTagId.trim() : '';
  } catch {
    return '';
  }
}

function loadGoogleTag(tagId: string): void {
  if (!isCanonicalProduction() || !/^(?:G|GT)-[A-Z0-9]+$/u.test(tagId)) return;
  if (document.getElementById(GOOGLE_TAG_SCRIPT_ID)) return;

  const analyticsWindow = window as AnalyticsWindow;
  analyticsWindow.dataLayer ??= [];
  analyticsWindow.gtag = (...args: unknown[]) => {
    analyticsWindow.dataLayer?.push(args);
  };
  analyticsWindow.gtag('js', new Date());
  analyticsWindow.gtag('config', tagId);

  const script = document.createElement('script');
  script.id = GOOGLE_TAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(tagId)}`;
  script.dataset.learnitiveAnalytics = 'google-tag';
  document.head.appendChild(script);
}

function XSocialIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231 5.45-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
    </svg>
  );
}

function CookieNotice({ onAccept, onDecline }: { onAccept: () => void; onDecline: () => void }) {
  return (
    <section className="interactive-cookie-notice" aria-label="Cookie preferences" data-interactive-cookie-notice="true">
      <p>
        Our website uses cookies. You can accept optional analytics cookies to help us understand and improve Learnitive. See our{' '}
        <a href="/privacy">Privacy Policy</a>.
      </p>
      <div className="interactive-cookie-actions" role="group" aria-label="Cookie preference actions">
        <button type="button" className="interactive-cookie-button interactive-cookie-button-primary" onClick={onAccept}>Accept cookies</button>
        <button type="button" className="interactive-cookie-button interactive-cookie-button-secondary" onClick={onDecline}>Decline cookies</button>
      </div>
    </section>
  );
}

function StickyXCta() {
  return (
    <a
      href="https://www.x.com/learnitive"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Follow Learnitive on X at @learnitive — opens in a new tab"
      data-public-x-cta="true"
      data-nosnippet="true"
      data-analytics-event="social_x_click"
      data-analytics-placement="sticky-x-cta"
      data-analytics-platform="x"
      data-public-x-enhanced="true"
      className="public-x-cta is-visible"
    >
      <span className="public-x-cta-icon" aria-hidden="true"><XSocialIcon className="size-5" /></span>
      <span className="public-x-cta-label">@learnitive</span>
    </a>
  );
}

/**
 * Cross-surface public telemetry and consent shell.
 *
 * Statcounter is a production-wide non-admin measurement required by BF38 and
 * deliberately does not depend on the optional-cookie preference. Google Tag
 * is optional and loads only after explicit acceptance. Admin routes load
 * neither analytics provider and never render the cookie/social overlays.
 */
export function LearnitiveSiteTelemetry() {
  const [pathname, setPathname] = React.useState(currentPathname);
  const [preference, setPreference] = React.useState<CookiePreference>(readCookiePreference);
  const admin = isAdminPath(pathname);

  React.useEffect(() => {
    const synchronize = () => setPathname(currentPathname());
    window.addEventListener('popstate', synchronize);
    window.addEventListener('hashchange', synchronize);
    window.addEventListener(LEARNITIVE_LOCATION_CHANGE_EVENT, synchronize);
    return () => {
      window.removeEventListener('popstate', synchronize);
      window.removeEventListener('hashchange', synchronize);
      window.removeEventListener(LEARNITIVE_LOCATION_CHANGE_EVENT, synchronize);
    };
  }, []);

  React.useEffect(() => {
    if (admin) return;
    loadStatcounter(pathname);
  }, [admin, pathname]);

  React.useEffect(() => {
    if (admin || preference !== 'accepted' || !isCanonicalProduction()) return;
    let cancelled = false;
    void readGoogleTagId().then((tagId) => {
      if (!cancelled && tagId) loadGoogleTag(tagId);
    });
    return () => { cancelled = true; };
  }, [admin, preference]);

  if (admin) return null;
  const noticeVisible = preference === undefined;

  return (
    <>
      {noticeVisible ? (
        <CookieNotice
          onAccept={() => {
            writeCookiePreference('accepted');
            setPreference('accepted');
          }}
          onDecline={() => {
            writeCookiePreference('declined');
            setPreference('declined');
          }}
        />
      ) : null}
      {!noticeVisible && isMarketingPath(pathname) && !pathname.startsWith('/learn') ? <StickyXCta /> : null}
    </>
  );
}

export const LEARNITIVE_COOKIE_PREFERENCE_KEY = COOKIE_PREFERENCE_KEY;
